import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Payment, Receipt, Search } from '@mui/icons-material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { inventoryAPI, spAPI } from '../../services/api';
import DataTable, { StatusChip } from '../../components/common/DataTable';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const ORDER_STATUS_OPTIONS = ['draft', 'confirmed', 'processing', 'paid', 'cancelled', 'returned'];
const NEW_CUSTOMER_OPTION_ID = '__new_sp_customer__';
const filterCustomers = createFilterOptions();

const defaultOrderValues = {
  customer_id: '',
  warehouse_id: '',
  order_type: 'retail',
  discount_amount: 0,
  tax_percent: 0,
  notes: '',
  items: [{ product_id: '', quantity: 1, unit_price: 0, discount_percent: 0 }],
};

const CreateOrderDialog = ({ open, onClose, onSaved, initialCustomer }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: defaultOrderValues });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const items = watch('items') || [];
  const orderType = watch('order_type');
  const customerOptions = useMemo(() => ([
    ...customers,
    { id: NEW_CUSTOMER_OPTION_ID, name: 'New Customer', customer_code: 'Create from Customers page' },
  ]), [customers]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    reset(defaultOrderValues);
    setBootstrapLoading(true);

    Promise.all([
      spAPI.getCustomers({ limit: 500 }),
      spAPI.getProducts({ limit: 1000 }),
      inventoryAPI.getWarehouses(),
    ])
      .then(([customersResponse, productsResponse, warehousesResponse]) => {
        if (!active) return;

        const loadedCustomers = customersResponse.data.data || [];
        if (initialCustomer?.id && !loadedCustomers.some((customer) => customer.id === initialCustomer.id)) {
          loadedCustomers.unshift(initialCustomer);
        }

        setCustomers(loadedCustomers);
        setProducts(productsResponse.data.data || []);
        setWarehouses(warehousesResponse.data.data || []);

        if (initialCustomer?.id) {
          setValue('customer_id', initialCustomer.id, { shouldValidate: true, shouldDirty: true });
        }
      })
      .catch(() => {
        if (active) toast.error('Failed to load spare-parts order setup');
      })
      .finally(() => {
        if (active) setBootstrapLoading(false);
      });

    return () => {
      active = false;
    };
  }, [initialCustomer, open, reset, setValue]);

  const subtotal = items.reduce((sum, item) => {
    const quantity = parseInt(item.quantity, 10) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    return sum + (quantity * unitPrice);
  }, 0);
  const discount = parseFloat(watch('discount_amount')) || 0;
  const taxPercent = parseFloat(watch('tax_percent')) || 0;
  const tax = Math.max(0, subtotal - discount) * taxPercent / 100;
  const total = subtotal - discount + tax;

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      await spAPI.createOrder({
        customer_id: data.customer_id || null,
        warehouse_id: data.warehouse_id,
        order_type: data.order_type || 'retail',
        discount_amount: parseFloat(data.discount_amount) || 0,
        tax_percent: parseFloat(data.tax_percent) || 0,
        notes: data.notes || '',
        items: (data.items || []).map((item) => ({
          product_id: item.product_id,
          quantity: parseInt(item.quantity, 10) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
        })),
      });
      toast.success('Spare-parts order created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Create Spare-Parts Order</DialogTitle>
        <DialogContent dividers>
          {bootstrapLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="customer_id"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={customerOptions}
                        filterOptions={(options, params) => {
                          const filtered = filterCustomers(options.filter((option) => option.id !== NEW_CUSTOMER_OPTION_ID), params);
                          filtered.push(customerOptions[customerOptions.length - 1]);
                          return filtered;
                        }}
                        getOptionLabel={(option) => (
                          option.id === NEW_CUSTOMER_OPTION_ID
                            ? 'New Customer'
                            : `${option.name} (${option.customer_code})`
                        )}
                        value={customerOptions.find((customer) => customer.id === field.value) || null}
                        onChange={(_, value) => {
                          if (value?.id === NEW_CUSTOMER_OPTION_ID) {
                            onClose();
                            navigate('/sp/customers', {
                              state: {
                                openCreateCustomer: true,
                                returnTo: '/sp/orders',
                                returnSystem: 'spare_parts',
                                reopenOrder: true,
                              },
                            });
                            return;
                          }

                          field.onChange(value?.id || '');
                        }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.id === NEW_CUSTOMER_OPTION_ID
                              ? 'New Customer'
                              : `${option.name} (${option.customer_code})`}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Customer"
                            helperText="Optional for walk-in sales"
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="warehouse_id"
                    control={control}
                    rules={{ required: 'Warehouse is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth size="small" error={!!fieldState.error}>
                        <InputLabel>Warehouse *</InputLabel>
                        <Select {...field} label="Warehouse *">
                          <MenuItem value=""><em>- Select Warehouse -</em></MenuItem>
                          {warehouses.map((warehouse) => (
                            <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                          ))}
                        </Select>
                        {fieldState.error && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Order Type</InputLabel>
                    <Select {...register('order_type')} label="Order Type" defaultValue="retail">
                      <MenuItem value="retail">Retail</MenuItem>
                      <MenuItem value="wholesale">Wholesale</MenuItem>
                      <MenuItem value="workshop">Workshop</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Notes" {...register('notes')} />
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Order Items
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Part</TableCell>
                      <TableCell width={100}>Qty</TableCell>
                      <TableCell width={140}>Unit Price</TableCell>
                      <TableCell width={120}>Discount %</TableCell>
                      <TableCell width={120}>Line Total</TableCell>
                      <TableCell width={40}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((field, idx) => {
                      const lineQty = parseInt(items[idx]?.quantity, 10) || 0;
                      const linePrice = parseFloat(items[idx]?.unit_price) || 0;
                      const lineDiscount = parseFloat(items[idx]?.discount_percent) || 0;
                      const lineTotal = lineQty * linePrice * (1 - (lineDiscount / 100));

                      return (
                        <TableRow key={field.id}>
                          <TableCell sx={{ minWidth: 260 }}>
                            <Controller
                              name={`items.${idx}.product_id`}
                              control={control}
                              rules={{ required: 'Part required' }}
                              render={({ field: productField }) => (
                                <Autocomplete
                                  size="small"
                                  options={products}
                                  getOptionLabel={(option) => `${option.name} (${option.sku})`}
                                  value={products.find((product) => product.id === productField.value) || null}
                                  onChange={(_, value) => {
                                    productField.onChange(value?.id || '');
                                    if (value) {
                                      const selectedPrice = orderType === 'wholesale'
                                        ? (value.wholesale_price || value.selling_price)
                                        : value.selling_price;
                                      setValue(`items.${idx}.unit_price`, parseFloat(selectedPrice) || 0);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      placeholder="Select part"
                                      error={!!errors?.items?.[idx]?.product_id}
                                      helperText={errors?.items?.[idx]?.product_id?.message}
                                    />
                                  )}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              sx={{ width: 80 }}
                              {...register(`items.${idx}.quantity`, { valueAsNumber: true, min: 1 })}
                              inputProps={{ min: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              sx={{ width: 120 }}
                              {...register(`items.${idx}.unit_price`, { valueAsNumber: true })}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              sx={{ width: 90 }}
                              {...register(`items.${idx}.discount_percent`, { valueAsNumber: true, min: 0, max: 100 })}
                              inputProps={{ min: 0, max: 100 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{fmt(lineTotal)}</TableCell>
                          <TableCell>
                            {fields.length > 1 && (
                              <IconButton size="small" color="error" onClick={() => remove(idx)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <Button
                  size="small"
                  startIcon={<Add />}
                  sx={{ mt: 1 }}
                  onClick={() => append({ product_id: '', quantity: 1, unit_price: 0, discount_percent: 0 })}
                >
                  Add Part
                </Button>
              </Paper>

              <Grid container justifyContent="flex-end">
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ bgcolor: '#fff7ed', borderRadius: 2, p: 2, border: '1px solid #fdba74' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmt(subtotal)}</Typography>
                    </Box>
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          size="small"
                          label="Discount"
                          type="number"
                          {...register('discount_amount', { valueAsNumber: true, min: 0 })}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          size="small"
                          label="Tax %"
                          type="number"
                          {...register('tax_percent', { valueAsNumber: true, min: 0 })}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="success.main">Discount: -{fmt(discount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Tax: {fmt(tax)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight={700}>Total:</Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="warning.dark">{fmt(total)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<Receipt />} disabled={saving || bootstrapLoading}>
            {saving ? <CircularProgress size={20} /> : 'Create Order'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SparePartsOrders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await spAPI.getOrders({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setOrders(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch {
      toast.error('Failed to load spare-parts orders');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (!location.state?.openCreateOrder && !location.state?.createdCustomer) return;

    setCreatedCustomer(location.state?.createdCustomer || null);
    setCreateOpen(location.state?.openCreateOrder !== false);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const quickPay = async (id) => {
    try {
      const order = orders.find((item) => item.id === id);
      if (!order?.balance_due) return;

      await spAPI.recordPayment(id, { amount: order.balance_due });
      toast.success('Payment recorded');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    }
  };

  const columns = [
    { key: 'order_number', label: 'Order #', render: (value) => <Typography fontFamily="monospace" fontWeight={700} fontSize="0.82rem">{value}</Typography> },
    { key: 'customer_name', label: 'Customer', render: (value) => value || 'Walk-in customer' },
    { key: 'salesperson_name', label: 'Salesperson' },
    { key: 'order_type', label: 'Type', render: (value) => <StatusChip status={value} /> },
    { key: 'item_count', label: 'Items', align: 'right' },
    { key: 'total_amount', label: 'Total', align: 'right', render: (value) => <Typography fontWeight={700}>{fmt(value)}</Typography> },
    { key: 'paid_amount', label: 'Paid', align: 'right', render: (value) => fmt(value) },
    { key: 'balance_due', label: 'Balance', align: 'right', render: (value) => <Typography color={parseFloat(value) > 0 ? 'error.main' : 'success.main'} fontWeight={600}>{fmt(value)}</Typography> },
    { key: 'payment_status', label: 'Payment', render: (value) => <StatusChip status={value} /> },
    { key: 'status', label: 'Order', render: (value) => <StatusChip status={value} /> },
    {
      key: 'id',
      label: 'Actions',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => navigate(`/sp/orders/${value}`)}>
            View
          </Button>
          {row.balance_due > 0 && row.status !== 'cancelled' && row.status !== 'returned' && (
            <Button size="small" variant="contained" startIcon={<Payment />} onClick={() => quickPay(value)}>
              Pay
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Parts Orders</Typography>
          <Typography variant="body2" color="text.secondary">{total} total orders</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          New Order
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search orders or customers..."
          size="small"
          sx={{ minWidth: 280 }}
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(event) => { setStatusFilter(event.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {ORDER_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={columns}
        rows={orders}
        loading={loading}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
        emptyMessage="No spare-parts orders found"
      />

      <CreateOrderDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreatedCustomer(null);
        }}
        onSaved={fetchOrders}
        initialCustomer={createdCustomer}
      />
    </Box>
  );
};

export default SparePartsOrders;
